import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, ArrowRight, Scale, ShieldCheck, Briefcase, Landmark } from "lucide-react";

export const Route = createFileRoute("/guides")({
  head: () => ({
    meta: [
      { title: "Plain-English Legal Guides for South Africans | Nkadimeng Danny Legal" },
      {
        name: "description",
        content:
          "Free, plain-English guides on CCMA preparation, POPIA compliance for small businesses, employee rights at disciplinary hearings and Court Online South Africa — written for non-lawyers.",
      },
      { property: "og:title", content: "Legal Guides — Nkadimeng Danny Legal" },
      {
        property: "og:description",
        content:
          "Free guides for South Africans navigating labour, compliance and court processes.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/guides" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/guides" }],
  }),
  component: GuidesIndex,
});

const guides = [
  {
    slug: "ccma-preparation",
    icon: Scale,
    tag: "Labour",
    title: "How to prepare for a CCMA hearing",
    summary:
      "A practical, step-by-step guide to referring a dispute, preparing your bundle and what to expect at conciliation and arbitration.",
  },
  {
    slug: "ccma-referral-guide",
    icon: Scale,
    tag: "Labour",
    title: "CCMA referral guide: completing LRA Form 7.11",
    summary:
      "How to refer a dispute to the CCMA: time limits, completing Form 7.11 block by block, proof of service, condonation and what happens next.",
  },
  {
    slug: "popia-for-smes",
    icon: ShieldCheck,
    tag: "Compliance",
    title: "Understanding POPIA for small businesses",
    summary:
      "What the Protection of Personal Information Act actually requires from a small South African business — explained without the jargon.",
  },
  {
    slug: "employee-rights-disciplinary",
    icon: Briefcase,
    tag: "Labour",
    title: "Employee rights during disciplinary hearings",
    summary:
      "Your procedural and substantive rights under Schedule 8 of the LRA, and how to use them without antagonising your employer.",
  },
  {
    slug: "court-online-explained",
    icon: Landmark,
    tag: "Courts",
    title: "Court Online South Africa explained",
    summary:
      "What Court Online and CaseLines are, who has to use them, and how filings actually work in the High Court today.",
  },
] as const;

function GuidesIndex() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <p className="text-xs uppercase tracking-[0.25em] text-gold">
            Free legal guides
          </p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            Plain-English guides for South Africans
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Written for people who aren't lawyers. Use them to understand the
            process, prepare your own paperwork, or decide when it's time to
            ask for professional help.
          </p>
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="grid gap-5 md:grid-cols-2">
          {guides.map((g) => (
            <Link
              key={g.slug}
              to={"/guides/$slug" as any}
              params={{ slug: g.slug } as any}
              className="group rounded-2xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-0.5 hover:shadow-lifted"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-navy text-gold">
                  <g.icon className="h-5 w-5" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-gold">
                  {g.tag}
                </span>
              </div>
              <h2 className="mt-4 font-display text-xl text-navy">{g.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {g.summary}
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-navy group-hover:text-gold">
                Read the guide <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="container-narrow pb-20">
        <div className="rounded-2xl border-l-4 border-gold bg-secondary/60 p-6 md:p-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold">
            <BookOpen className="h-4 w-4" /> Important
          </div>
          <p className="mt-2 text-sm text-muted-foreground md:text-base">
            These guides are general information, not legal advice, and do not
            create an attorney-client relationship. For advice on your
            specific matter, book a consultation or speak to an admitted
            attorney.
          </p>
        </div>
      </section>
    </>
  );
}
